/**
 * Sends a single element to the printer, on its own.
 *
 * The element is serialised into a hidden same-origin iframe that holds nothing
 * else: no navbar, no toolbar, and none of the app's stylesheets. That is what
 * makes the printout predictable — the iframe's own `@page` rule decides the
 * paper and the margins, and no app CSS can reach in and shift the layout.
 *
 * The price of that isolation is that the element must carry its own styling
 * inline: anything left behind in a stylesheet does not travel with
 * `outerHTML`. The poster is built that way on purpose.
 */

/**
 * How long the iframe is kept before it is torn down anyway. Browsers fire
 * `afterprint` when the dialog closes, but not all of them do it reliably, and
 * an orphaned iframe would leak for as long as the page is open.
 */
const TEARDOWN_FALLBACK_MS = 60_000;

/**
 * How long the images are waited for. Printing before a logo has landed prints
 * its gap, but a URL that never answers must not block the dialog forever.
 */
const IMAGE_TIMEOUT_MS = 4_000;

export interface PrintNodeOptions {
  /** The print document's title — what "Save as PDF" offers as the filename. */
  title?: string;
  /** The `@page size` value, e.g. `A4 portrait`. */
  pageSize?: string;
  /**
   * The `@page margin` value. None by default — a poster draws its own — but a
   * sheet that runs over several pages needs it, or its later pages start at
   * the paper's edge.
   */
  pageMargin?: string;
  /**
   * Rules for the paper alone, added to the print document's stylesheet —
   * what an element styled inline cannot say, such as a strip pinned to the
   * foot of every page (`position: fixed` there, not on screen).
   */
  css?: string;
}

/** Escapes text going into the iframe's `<title>` / `<base href>`. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function waitForImages(doc: Document): Promise<void> {
  const pending = Array.from(doc.images).filter(image => !image.complete);
  if (pending.length === 0) return Promise.resolve();

  const loaded = Promise.all(
    pending.map(
      image =>
        new Promise<void>(resolve => {
          image.addEventListener('load', () => resolve(), { once: true });
          image.addEventListener('error', () => resolve(), { once: true });
        })
    )
  ).then(() => undefined);

  const timeout = new Promise<void>(resolve => {
    window.setTimeout(resolve, IMAGE_TIMEOUT_MS);
  });

  return Promise.race([loaded, timeout]);
}

export async function printNode(node: HTMLElement, options: PrintNodeOptions = {}): Promise<void> {
  const { title = document.title, pageSize = 'A4 portrait', pageMargin = '0', css = '' } = options;

  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.setAttribute('title', title);
  frame.style.cssText = 'position:fixed;left:-9999px;top:0;width:1px;height:1px;border:0;opacity:0;';
  document.body.appendChild(frame);

  const doc = frame.contentDocument;
  const win = frame.contentWindow;
  if (!doc || !win) {
    frame.remove();
    return;
  }

  // `<base>` so the poster's relative image URLs still resolve: an iframe with
  // no src inherits the opener's origin but is easier to reason about pinned.
  doc.open();
  doc.write(
    `<!doctype html><html><head><meta charset="utf-8">` +
      `<base href="${escapeHtml(document.baseURI)}">` +
      `<title>${escapeHtml(title)}</title><style>` +
      `@page { size: ${pageSize}; margin: ${pageMargin}; }` +
      `html, body { margin: 0; padding: 0; background: #fff; }` +
      // Browsers drop background colours from printouts unless told otherwise,
      // which would leave the poster's tinted blocks blank.
      `*, *::before, *::after { box-sizing: border-box;` +
      ` -webkit-print-color-adjust: exact; print-color-adjust: exact; }` +
      css +
      `</style></head><body>${node.outerHTML}</body></html>`
  );
  doc.close();

  await waitForImages(doc);

  let tornDown = false;
  const teardown = () => {
    if (tornDown) return;
    tornDown = true;
    frame.remove();
  };
  win.addEventListener('afterprint', teardown, { once: true });
  window.setTimeout(teardown, TEARDOWN_FALLBACK_MS);

  win.focus();
  win.print();
}
